import { Eye, EyeOff, Loader2 } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useSearchParams } from "react-router"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"

import { AuthLayout } from "./auth-layout"
import { useCurrentUser, useResetPasswordMutation, type ResetPasswordError } from "./auth-store"

const MIN_PASSWORD_LENGTH = 8

interface FieldErrors {
  password: string | null
  confirm: string | null
}

function validate(password: string, confirm: string): FieldErrors {
  return {
    password:
      password.length === 0
        ? "Choose a new password."
        : password.length < MIN_PASSWORD_LENGTH
          ? `At least ${MIN_PASSWORD_LENGTH} characters.`
          : null,
    confirm: confirm !== password ? "Passwords do not match." : null,
  }
}

function hasError(errors: FieldErrors): boolean {
  return errors.password !== null || errors.confirm !== null
}

export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  const reset = useResetPasswordMutation()
  const currentUser = useCurrentUser()

  // A signed-in reviewer who lands here with a token is completing a reset for
  // *their* mailbox — accepting will replace the current session, so let them
  // through. Without a token there is nothing to complete; bounce to /reviews.
  React.useEffect(() => {
    if (currentUser && !token) navigate("/reviews", { replace: true })
  }, [currentUser, token, navigate])

  const [password, setPassword] = React.useState("")
  const [confirm, setConfirm] = React.useState("")
  const [showPassword, setShowPassword] = React.useState(false)
  const [attempted, setAttempted] = React.useState(false)

  const fieldErrors = React.useMemo<FieldErrors>(() => {
    if (!attempted) return { password: null, confirm: null }
    return validate(password, confirm)
  }, [attempted, password, confirm])

  if (!token) {
    return (
      <AuthLayout
        title="Reset link missing"
        subtitle="This page expects a reset token in the URL."
        footer={
          <p>
            <Link to="/forgot-password" className="text-brand hover:underline">
              Request a new link
            </Link>
          </p>
        }
      >
        <Alert tone="error" size="sm" title="No reset token">
          Open the link from your email, or request a new one.
        </Alert>
      </AuthLayout>
    )
  }

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setAttempted(true)
    const errors = validate(password, confirm)
    if (hasError(errors) || reset.isPending) return
    try {
      await reset.mutateAsync({ token, newPassword: password })
      navigate("/reviews", { replace: true })
    } catch {
      /* surfaced via reset.error */
    }
  }

  const topError = toTopError(reset.error)

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Pick something you haven't used before. Any active sessions on other devices will be signed out."
      footer={
        <p>
          <Link to="/login" className="text-text-2 hover:text-text">
            Back to sign in
          </Link>
        </p>
      }
    >
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        noValidate
        aria-busy={reset.isPending || undefined}
      >
        <div className="sr-only" aria-live="polite" role="status">
          {reset.isPending ? "Setting your new password…" : topError ? topError.body : ""}
        </div>

        {topError && (
          <Alert tone="error" size="sm" title={topError.title}>
            {topError.body}
            {topError.canRequestNew && (
              <>
                {" "}
                <Link to="/forgot-password" className="underline">
                  Request a new link
                </Link>
                .
              </>
            )}
          </Alert>
        )}

        {currentUser && (
          <Alert tone="warning" size="sm" title={`Signed in as ${currentUser.email}`}>
            Completing this reset will replace your current session.
          </Alert>
        )}

        <FormField label="New password" required error={fieldErrors.password}>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              inputSize="lg"
              value={password}
              onChange={(e) => {
                setPassword(e.currentTarget.value)
                if (reset.error) reset.reset()
              }}
              disabled={reset.isPending}
              wrapperClassName="pr-9"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              tabIndex={-1}
              disabled={reset.isPending}
              className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-sm text-text-3 transition-colors duration-120 ease-out hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)] disabled:opacity-50"
            >
              {showPassword ? (
                <EyeOff className="size-4" aria-hidden="true" />
              ) : (
                <Eye className="size-4" aria-hidden="true" />
              )}
            </button>
          </div>
        </FormField>

        <FormField label="Confirm password" required error={fieldErrors.confirm}>
          <Input
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            inputSize="lg"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.currentTarget.value)
              if (reset.error) reset.reset()
            }}
            disabled={reset.isPending}
          />
        </FormField>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          leadingIcon={reset.isPending ? <Loader2 className="animate-spin" /> : undefined}
          disabled={reset.isPending}
        >
          {reset.isPending ? "Setting new password…" : "Set new password and sign in"}
        </Button>
      </form>
    </AuthLayout>
  )
}

interface TopError {
  title: string
  body: string
  canRequestNew: boolean
}

function toTopError(error: ResetPasswordError | null): TopError | null {
  if (!error) return null
  switch (error.kind) {
    case "invalid_or_expired":
      return {
        title: "Reset link invalid or expired",
        body: "This link no longer works.",
        canRequestNew: true,
      }
    case "network":
      return {
        title: "Network problem",
        body: "We couldn't reach the server. Check your connection and try again.",
        canRequestNew: false,
      }
    case "server":
      return {
        title: "Server error",
        body: "Something went wrong on our side. Please try again in a moment.",
        canRequestNew: false,
      }
    default:
      return { title: "Could not reset password", body: error.message, canRequestNew: false }
  }
}
